<template>
  <organism-configuration-history
    v-if="filterData"
    :filterList="filterData"
    :nodeOptions="nodeOptions"
    :nodeVersionOptions="nodeVersionOptions"
    :fields="fields"
    :items="historyList"
    :selectedConfigData="selectedConfigData"
    @onUpdateFilterData="onUpdateFilterData"
    @onUpdateCurrentPage="onUpdateCurrentPage"
    @onSetDefaultFilterData="onSetDefaultFilterData"
    @clickCompare="clickCompare"
    @onSelectedConfigData="onSelectedConfigData"
    @onUpdateFilterSelectedNode="onUpdateFilterSelectedNode"
  ></organism-configuration-history>
</template>

<script>
import OrganismConfigurationHistory from "@/components/organisms/networkConfigurationManagement/OrganismConfigurationHistory.vue"

import {
  getConfigurationChangeList,
  getNodeList,
  getConfigVersionWithTimestamp,
  getNodeConfigurationDetail,
} from "@/api/api-module4"
import { DateTime } from "luxon"
import { mapActions, mapGetters } from "vuex"
import mixinsDatetime from "@/mixins/mixinsDatetime"
import mixins from "@/mixins"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"

export default {
  components: { OrganismConfigurationHistory },
  name: "ConfigurationHistory",
  mixins: [mixins, mixinsDatetime, mixinsObjectHelper],
  data() {
    return {
      fields: [
        {
          key: "timestamp",
          label: "TIMESTAMP",
          thClass: "w-200px",
          tdClass: "w-200px",
        },
        {
          key: "nodeName",
          label: "NODE NAME",
          thClass: "w-300px",
          tdClass: "w-300px",
        },
        {
          key: "ipAddress",
          label: "IP ADDRESS",
          thClass: "w-200px",
          tdClass: "w-200px",
        },
        {
          key: "version",
          label: "VERSION",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
        {
          key: "configButton",
          label: "",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
      ],
      historyList: [],
      nodeOptions: [],
      nodeVersionOptions: [],
      selectedConfigData: "",
      filterData: null,
    }
  },
  computed: {
    ...mapGetters("appFilter", ["getNCMConfigHistoryFilter"]),
  },
  mounted() {
    this.getConfigurationChangeFromApi()
    this.getNodeOptionsFromApi()
  },
  methods: {
    ...mapActions("appFilter", ["setNCMConfigHistoryFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getNCMConfigHistoryFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    clickCompare(data) {
      if (data.version !== 1) {
        this.$router.replace({
          path: "/network-configuration/configuration-comparison",
          query: {
            BaselineNodeId: data.nodeId,
            BaselineVersion: data.version - 1,
            CompareToNodeId: data.nodeId,
            CompareToVersion: data.version,
          },
        })
      } else {
        this.$router.replace({
          path: "/network-configuration/configuration-comparison",
          query: {
            BaselineNodeId: data.nodeId,
            BaselineVersion: data.version,
          },
        })
      }
    },

    onUpdateFilterSelectedNode(nodeId) {
      this.getConfigurationChangeVersionFromApi(nodeId)
    },

    onSelectedConfigData(data) {
      const nodeId = data.nodeId
      const version = data.version
      this.getConfigurationDataFromApi(nodeId, version)
    },

    async getConfigurationChangeFromApi() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = this.filterDecoration()
      await getConfigurationChangeList(filterData)
        .then((response) => {
          this.filterData.total = response.totalCount
          const updatedFilter = {
            ...this.getNCMConfigHistoryFilter,
            total: response.totalCount,
          }
          this.setNCMConfigHistoryFilter(updatedFilter)
          this.historyList = response.data.map((history) => {
            return {
              nodeId: history.nodeId,
              version: history.version,
              timeStamp: history.timestamp,
              timestamp: {
                date: DateTime.fromISO(history.timestamp).toFormat(
                  "dd/MM/yyyy"
                ),
                time: DateTime.fromISO(history.timestamp).toFormat("HH:mm:ss"),
              },
              ipAddress: history.ipAddress,
              nodeName: history.hostname,
            }
          })
        })
        .finally(() => this.showLoading(false))
    },

    filterDecoration() {
      const filter = {
        PageSize: 10,
        PageNumber: this.filterData.currentPage,
        From: this.filterData.isEnableDate
          ? this.removeTimeZone(this.filterData.dateTime.start)
          : undefined,
        To: this.filterData.isEnableDate
          ? this.removeTimeZone(this.filterData.dateTime.end)
          : undefined,
        NodeId: this.filterData.node ? this.filterData.node.nodeId : undefined,
        Version: this.filterData?.version?.version || undefined,
      }
      return filter
    },

    // click filter
    onUpdateFilterData(filter) {
      filter.currentPage = 1
      this.setNCMConfigHistoryFilter(filter)
      this.getConfigurationChangeFromApi()
    },

    onUpdateCurrentPage(filter) {
      const updatedFilter = {
        ...this.getNCMConfigHistoryFilter,
        currentPage: filter.currentPage,
      }
      this.setNCMConfigHistoryFilter(updatedFilter)
      this.getConfigurationChangeFromApi()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        node: null,
        version: null,
        dateTime: {
          start: "",
          end: "",
          now: "",
        },
        isEnableDate: false,
        currentPage: 1,
        total: 1,
      }
      this.setNCMConfigHistoryFilter(defaultFilter)
      this.getConfigurationChangeFromApi()
    },

    async getNodeOptionsFromApi() {
      this.showLoading(true)
      const filter = {
        PageSize: 100,
      }
      await getNodeList(filter)
        .then((response) => {
          if (response) {
            this.nodeOptions = response.data.map((node) => {
              return {
                nodeId: node.nodeId,
                nodeValue: `${node.hostname} (${node.ipAddress})`,
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getConfigurationChangeVersionFromApi(nodeId) {
      this.showLoading(true)
      await getConfigVersionWithTimestamp(nodeId)
        .then((response) => {
          if (response) {
            this.nodeVersionOptions = response.data.map((config) => {
              return {
                label: `Version ${config.version} (${this.setDateTimeFormat(
                  config.timestamp,
                  "dd/MM/yyyy HH:mm"
                )})`,
                version: config.version,
                timestamp: config.timestamp,
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getConfigurationDataFromApi(nodeId, version) {
      this.showLoading(true)
      await getNodeConfigurationDetail(nodeId, version)
        .then((response) => {
          if (response) {
            this.selectedConfigData = response.data.configuration
          }
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped></style>
